import XngMiniCart from "@eisinc/App/Components/XngMiniCart/XngMiniCart";
import { xHeaderState } from "@eisinc/App/Store/Reducers";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import ShoppingCart from "@eisinc/XDL/Icons/ShoppingCart";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// eslint-disable-next-line spire/fenced-imports
import mixpanel from "mixpanel-browser";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";


const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState & xHeaderState) => {
    const cart = getCurrentCartState(state).value;

    return {
        cart,
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        isSticky: state.xHeader.isSticky,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: InjectableCss;
    cartTotalStyles?: InjectableCss;
    cartIconStyles?: InjectableCss;
    cartIconCountWrapper?: InjectableCss;
    cartCountStyles?: InjectableCss;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        css: css`
            flex-direction: column;
            &:hover,
            &:focus {
                text-decoration: none;
            }
        `,
    },
    cartTotalStyles: {
        css: css`
            font-size: 14px;
            text-align: center;
            font-weight: normal;
            color: ${getColor("text.main")};

            &.sticky {
                font-size: 12px;
                line-height: 16px;
            }
        `,
    },
    cartIconCountWrapper: {
        css: css`
            position: relative;
        `,
    },
    cartCountStyles: {
        css: css`
            position: absolute;
            top: -12px;
            left: 7px;
            background: ${getColor("primary.main")};
            min-width: 22px;
            min-height: 22px;
            border-radius: 50%;
            color: ${getColor("primary.contrast")};
            text-align: center;
            padding: 0 2px;

            & span {
                font-size: 13px;
                line-height: 16px;
            }

            &.sticky {
                top: -12px;
                min-width: 22px;
                min-height: 14px;

                & span {
                    font-size: 12px;
                }
            }
        `,
    },
    cartIconStyles: {
        css: css`
            & > svg {
                fill: ${getColor("text.main")};
            }
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

interface CartCountProps {
    lineCount: number | undefined;
    icon: any;
    isSticky: boolean;
}

const CartCount = ({ lineCount, icon, isSticky }: CartCountProps) => {
    return (
        <StyledWrapper {...cartLinkStyles.cartIconCountWrapper}>
            <StyledWrapper
                data-test-selector="cartLinkQuantity"
                {...cartLinkStyles.cartCountStyles}
                className={isSticky ? "sticky" : ""}
            >
                <Typography as="span">{lineCount || 0}</Typography>
            </StyledWrapper>
            <Icon src={icon} color="text.main" size={isSticky ? 24 : 30} {...cartLinkStyles.cartIconStyles} />
        </StyledWrapper>
    );
};

interface CartTotalProps {
    cartTotal: string | undefined;
    isSticky: boolean;
}

const CartTotal = ({ cartTotal, isSticky }: CartTotalProps) => {
    return (
        <Hidden below="md" as="span" {...cartLinkStyles.cartTotalStyles} className={isSticky ? "sticky" : ""}>
            {cartTotal}
        </Hidden>
    );
};

const iconMap = {
    cart: ShoppingCart,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    isSticky,
    cart,
    orderSubTotalDisplay,
    fields: { icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[selectedIcon];

    const { routerLink, cartTotalStyles } = styles;
    const cartSubTotal = orderSubTotalDisplay;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    const MiniCartButton = (
        <StyledWrapper>
            <Clickable {...routerLink} href={cartUrl} data-test-selector="cartLink">
                <CartCount lineCount={retainedTotalCountToDisplay} isSticky={isSticky} icon={icon} />
                <CartTotal cartTotal={cartSubTotal} isSticky={isSticky} {...cartTotalStyles} />
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Clickable>
        </StyledWrapper>
    );

    const onOpen = (popup: any) => {
        // eslint-disable-next-line no-prototype-builtins
        if ((window as any).hasOwnProperty("mixpanelInitialized")) {
            mixpanel.track("Mini-Cart Pageview");
        }
    };

    return (
        <StyledWrapper>
            <Hidden below="lg">
                <StyledWrapper>
                    <Tooltip
                        trigger="mouseenter focus"
                        placement="bottom-end"
                        triggerAltText={translate("Mini Cart Menu")}
                        triggerComponent={MiniCartButton}
                        delay={[200, 550]}
                        offset={[0, 8]}
                        showBlackArrow={true}
                        cssOverrides={{
                            tooltipBody: css``,
                            tooltipContainer: css`
                                max-width: initial;
                                width: 640px;
                                padding: 0;
                                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
                                border-radius: 0;
                                border: 1px solid ${getColor("common.ui3")};
                                overflow: hidden;
                            `,
                        }}
                        passAsChildren={true}
                    >
                        <XngMiniCart />
                    </Tooltip>
                </StyledWrapper>
            </Hidden>
            <Hidden above="md">{MiniCartButton}</Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
